import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import "@fontsource/poppins";

export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
      grey: {
        50: "#ffffff",
        100: "#e6e6e6",
        200: "#cccccc",
        300: "#a3a3a3",
        400: "#808080",
        500: "#666666",
        600: "#4c4c4c",
        700: "#323232",
        800: "#1a1a1a",
        900: "#000000",
      },
      primary: {
        50: "#fbf4ff",
        100: "#f4e6ff",
        200: "#ebd1ff",
        300: "#e1b5ff",
        400: "#d8a1ff",
        500: "#b480ff",
        600: "#9a8cd0",
        700: "#896cfe",
        800: "#8200ee",
        900: "#5b00aa",
      },
      greenAccent: {
        100: "#dbf5ee",
        200: "#b7ebde",
        300: "#94e2cd",
        400: "#70d8bd",
        500: "#2F94FF",
        600: "#2F94FF",
        700: "#2e7c67",
        800: "#1e5245",
        900: "#0f2922",
      },
      redAccent: {
        100: "#f8dcdb",
        200: "#f1b9b7",
        300: "#e99592",
        400: "#e2726e",
        500: "#db4f4a",
        600: "#af3f3b",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",
      },
      blueAccent: {
        100: "#e1e2fe",
        200: "#c3c6fd",
        300: "#a4a9fc",
        400: "#868dfb",
        500: "#cdd1e4",
        600: "#535ac8",
        700: "#3e4396",
        800: "#2a2d64",
        900: "#151632",
      },
    }
    : {
      grey: {
        50: "#ffffff",
        100: "#e6e6e6",
        200: "#cccccc",
        300: "#a3a3a3",
        400: "#808080",
        500: "#666666",
        600: "#4c4c4c",
        700: "#323232",
        800: "#1a1a1a",
        900: "#000000",
      },
      primary: {
        50: "#fbf4ff",
        100: "#f4e6ff",
        200: "#ebd1ff",
        300: "#e1b5ff",
        400: "#d8a1ff",
        500: "#b480ff",
        600: "#9a8cd0",
        700: "#896cfe",
        800: "#8200ee",
        900: "#5b00aa",
      },
      greenAccent: {
        100: "#dbf5ee",
        200: "#b7ebde",
        300: "#94e2cd",
        400: "#70d8bd",
        500: "#2F94FF",
        600: "#2F94FF",
        700: "#2e7c67",
        800: "#1e5245",
        900: "#0f2922",
      },
      redAccent: {
        100: "#f8dcdb",
        200: "#f1b9b7",
        300: "#e99592",
        400: "#e2726e",
        500: "#db4f4a",
        600: "#af3f3b",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",
      },
      blueAccent: {
        100: "#e1e2fe",
        200: "#c3c6fd",
        300: "#a4a9fc",
        400: "#868dfb",
        500: "#cdd1e4",
        600: "#535ac8",
        700: "#3e4396",
        800: "#2a2d64",
        900: "#151632",
      }
    }),
});

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
          primary: {
            main: colors.primary[800],
          },
          secondary: {
            main: colors.primary[600],
          },
          error:{
            main: colors.primary[300]
          },
          success:{
            main: colors.primary[100]
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: "#191919",
          },
        }
        : {
          primary: {
            main: colors.primary[800],
          },
          secondary: {
            main: colors.primary[600],
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: colors.grey[900],
          },
        }),
    },
    typography: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

export const ColorModeContext = createContext({
  toggleColorMode: () => { },
});

export const useMode = () => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
